import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { StatusBadge } from "@/components/kit";
import { color } from "@/lib/theme";

const PIPELINE = [
  { k: "AI SYSTEM", v: "Customer Support Agent", tone: "brand", status: "active", label: "Demo" },
  { k: "SECURITY ASSESSMENT", v: "AI Red-Team Assessment", tone: "brand", status: "passed", label: "Assessed" },
  { k: "FINDINGS", v: "2 findings · mapped to controls", tone: "warn", status: "finding", label: "Finding" },
  { k: "EVIDENCE", v: "Evidence package generated", tone: "gold", status: "passed", label: "Ready" },
];

const CHIPS = ["ISO/IEC 42001", "EU AI Act", "NIST AI RMF", "OWASP LLM"];

export default function HeroVisual() {
  const [scan, setScan] = useState(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => setScan((s) => (s + 1) % (PIPELINE.length + 1)), 1400);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative mx-auto w-full max-w-[560px]">
      <div className="pointer-events-none absolute -inset-x-6 -top-10 z-20 hidden flex-wrap justify-center gap-2 sm:flex">
        {CHIPS.map((c, i) => (
          <motion.span
            key={c}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 + i * 0.15, duration: 0.6 }}
            className="rounded-full border border-line bg-panel-raised/80 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-content-muted backdrop-blur-md"
          >
            {c}
          </motion.span>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="relative overflow-hidden rounded-xl border border-line bg-panel-raised/70 p-5 backdrop-blur-xl sm:p-7"
      >
        <div className="absolute inset-0 tech-grid opacity-60" />
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-brand/[0.075] blur-3xl" />

        <div className="relative flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-col gap-1">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">Assurance Pipeline</span>
            <span className="font-mono text-[8.5px] uppercase tracking-[0.18em] text-brand-soft/80">Simulated environment</span>
          </div>
          <StatusBadge status="active">Platform demo</StatusBadge>
        </div>

        <div className="relative mt-6 flex flex-col gap-3">
          {PIPELINE.map((s, i) => {
            const scanning = scan === i;
            return (
              <motion.div
                key={s.k}
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + i * 0.18, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="relative flex items-center gap-3 rounded-md border px-3 py-3 transition-colors duration-500 sm:gap-4 sm:px-4"
                style={{
                  borderColor: scanning ? color(s.tone, 0.45) : color("line"),
                  background: scanning ? color(s.tone, 0.06) : color("panel"),
                }}
              >
                <span className="relative h-2 w-2 shrink-0 rounded-full" style={{ background: color(s.tone), boxShadow: `0 0 8px ${color(s.tone, 0.4)}` }}>
                  {scanning && <span className="absolute inset-0 animate-pulse-ring rounded-full" style={{ background: color(s.tone) }} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">{s.k}</div>
                  <div className="truncate text-[13px] text-content sm:text-sm">{s.v}</div>
                </div>
                <StatusBadge status={s.status} className="shrink-0">{s.label}</StatusBadge>
                {i < PIPELINE.length - 1 && <span className="absolute -bottom-3 left-[22px] h-3 w-px bg-line" />}
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.7, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative mt-5 flex items-center justify-between rounded-md border px-4 py-4 transition-colors duration-500"
          style={{
            borderColor: color("gold", scan === PIPELINE.length ? 0.5 : 0.25),
            background: `linear-gradient(90deg, ${color("gold", 0.08)}, transparent)`,
          }}
        >
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-content-faint">Meira Trust Passport</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-gradient-gold font-display text-3xl font-light leading-none">87</span>
              <span className="font-mono text-[10px] text-content-faint">/ 100</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusBadge status="verified" />
            <span className="font-mono text-[9px] tracking-wide text-content-faint">CERT-DEMO-000148</span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
